import { AircraftAlert } from './adsbTypes';

export type TcasShape = 'diamond' | 'circle' | 'square';
export type VerticalTrend = 'up' | 'down';

const ICON_SIZE = 24; // 20% smaller than original 32px canvas
const DIAMOND_WIDTH_RATIO = 0.8;

const SHAPE_BY_LEVEL: Record<string, TcasShape> = {
  INFO: 'diamond',
  WARNING: 'circle',
  CRITICAL: 'square',
};

const DEFAULT_SHAPE: TcasShape = 'circle';

export const VERTICAL_RATE_THRESHOLD_FPM = 500;

export interface MaskIconDescriptor {
  id: string;
  url: string;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
  mask: boolean;
}

const iconCache = new Map<string, MaskIconDescriptor>();

const svgToDataUrl = (svg: string): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

const buildShapeSvg = (shape: TcasShape, height: number, width = height): string => {
  const centerY = height / 2;

  if (shape === 'diamond') {
    const centerX = width / 2;
    const inset = 2;
    const points = `${centerX},${inset} ${width - inset},${centerY} ${centerX},${height - inset} ${inset},${centerY}`;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><polygon points="${points}" fill="white"/></svg>`;
  }

  const center = height / 2;

  if (shape === 'circle') {
    const radius = center - 2;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><circle cx="${center}" cy="${center}" r="${radius}" fill="white"/></svg>`;
  }

  const inset = 2;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect x="${inset}" y="${inset}" width="${width - inset * 2}" height="${height - inset * 2}" fill="white"/></svg>`;
};

// Same shaft + chevron-head arrow glyph as MUI's ArrowUpward/ArrowDownward
// icons (used for the off-screen traffic indicators), so both arrow styles
// match visually. Paths are defined on MUI's native 24x24 viewBox.
const ARROW_UP_PATH = 'm4 12 1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z';
const ARROW_DOWN_PATH = 'm20 12-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z';

const buildArrowSvg = (direction: VerticalTrend, size: number): string => {
  const path = direction === 'up' ? ARROW_UP_PATH : ARROW_DOWN_PATH;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24"><path d="${path}" fill="white"/></svg>`;
};

const getCachedMaskIcon = (
  id: string,
  svg: string,
  width = ICON_SIZE,
  height = ICON_SIZE
): MaskIconDescriptor => {
  const cached = iconCache.get(id);
  if (cached) return cached;

  const icon: MaskIconDescriptor = {
    id,
    url: svgToDataUrl(svg),
    width,
    height,
    anchorX: width / 2,
    anchorY: height / 2,
    mask: true,
  };
  iconCache.set(id, icon);
  return icon;
};

const shapeForAlertLevel = (alertLevel: string): TcasShape =>
  SHAPE_BY_LEVEL[alertLevel] ?? DEFAULT_SHAPE;

export const getShapeIcon = (alertLevel: string): MaskIconDescriptor => {
  const shape = shapeForAlertLevel(alertLevel);
  if (shape === 'diamond') {
    const height = ICON_SIZE;
    const width = height * DIAMOND_WIDTH_RATIO;
    return getCachedMaskIcon(
      `tcas-shape-${shape}`,
      buildShapeSvg(shape, height, width),
      width,
      height
    );
  }
  return getCachedMaskIcon(`tcas-shape-${shape}`, buildShapeSvg(shape, ICON_SIZE));
};

export const getVerticalRateArrowIcon = (direction: VerticalTrend): MaskIconDescriptor =>
  getCachedMaskIcon(`tcas-arrow-${direction}`, buildArrowSvg(direction, ICON_SIZE));

export const getVerticalTrend = (aircraft: AircraftAlert): VerticalTrend | null => {
  const geomRate = aircraft.geomRate;
  const baroRate = aircraft.baroRate;
  const rate =
    geomRate !== undefined && Math.abs(geomRate) >= VERTICAL_RATE_THRESHOLD_FPM
      ? geomRate
      : baroRate !== undefined && Math.abs(baroRate) >= VERTICAL_RATE_THRESHOLD_FPM
      ? baroRate
      : undefined;

  if (rate === undefined) return null;
  return rate > 0 ? 'up' : 'down';
};
